import { useMemo } from 'react';

import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';

/**
 * Section definitions grouped by tab category.
 * Must match backend ALL_PORTAL_SECTIONS.
 */
const SECTION_GROUPS = [
  {
    id: 'progress',
    label: 'Progress',
    children: [
      { id: 'progress.overview', label: 'Overview' },
      { id: 'progress.calendar', label: 'Calendar' },
      { id: 'progress.training', label: 'Training' },
      { id: 'progress.journey', label: 'Journey' },
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    children: [],
  },
  {
    id: 'learning',
    label: 'Learning',
    children: [
      { id: 'learning.overview', label: 'Overview' },
      { id: 'learning.vocabulary', label: 'Vocabulary' },
      { id: 'learning.quizzes', label: 'Quizzes' },
      { id: 'learning.scenarios', label: 'Scenarios' },
      { id: 'learning.badges', label: 'Badges' },
      { id: 'learning.leaderboard', label: 'Leaderboard' },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    children: [],
  },
];

// Flat list of all section IDs
const ALL_IDS = SECTION_GROUPS.flatMap(g =>
  g.children.length > 0
    ? [g.id, ...g.children.map(c => c.id)]
    : [g.id]
);

/**
 * Checkbox grid for selecting which portal sections are visible.
 * Used in Program editor and Student overrides.
 *
 * @param {string[]|null} value - current visible_portal_sections (null = all visible)
 * @param {function} onChange - called with new array or null
 */
const PortalSectionCheckboxGrid = ({ value, onChange }) => {
  const isRestricted = value !== null && value !== undefined;
  const selected = useMemo(() => new Set(value || []), [value]);

  const handleToggleRestrict = (checked) => {
    if (checked) {
      // Enable restrictions — start with all selected
      onChange([...ALL_IDS]);
    } else {
      // Disable restrictions — null means all visible
      onChange(null);
    }
  };

  const isChecked = (id) => selected.has(id);

  const toggleSection = (id) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
      // If removing a parent, also remove its children
      const group = SECTION_GROUPS.find(g => g.id === id);
      if (group) {
        group.children.forEach(c => next.delete(c.id));
      }
    } else {
      next.add(id);
      // If adding a child, ensure parent is included
      const parentGroup = SECTION_GROUPS.find(g => g.children.some(c => c.id === id));
      if (parentGroup) {
        next.add(parentGroup.id);
      }
    }
    onChange([...next]);
  };

  const toggleGroup = (group) => {
    const allChildIds = group.children.map(c => c.id);
    const allChecked = allChildIds.every(id => selected.has(id)) && selected.has(group.id);

    const next = new Set(selected);
    if (allChecked) {
      // Uncheck all in group
      next.delete(group.id);
      allChildIds.forEach(id => next.delete(id));
    } else {
      // Check all in group
      next.add(group.id);
      allChildIds.forEach(id => next.add(id));
    }
    onChange([...next]);
  };

  const getGroupState = (group) => {
    if (group.children.length === 0) {
      return selected.has(group.id) ? 'checked' : 'unchecked';
    }
    const childIds = group.children.map(c => c.id);
    const checkedCount = childIds.filter(id => selected.has(id)).length;
    if (checkedCount === 0 && !selected.has(group.id)) return 'unchecked';
    if (checkedCount === childIds.length && selected.has(group.id)) return 'checked';
    return 'indeterminate';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Switch
          id="restrict-sections"
          checked={isRestricted}
          onCheckedChange={handleToggleRestrict}
        />
        <Label htmlFor="restrict-sections" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Restrict portal sections
        </Label>
      </div>

      {isRestricted && (
        <div className="border border-stone-200 dark:border-stone-700 rounded-lg p-4 space-y-4 bg-stone-50/50 dark:bg-stone-900/30">
          <p className="text-xs text-stone-500 dark:text-stone-400">
            Only checked sections will be visible to students in this program.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SECTION_GROUPS.map((group) => {
              const groupState = getGroupState(group);
              return (
                <div key={group.id} className="space-y-2">
                  {/* Group header */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`section-${group.id}`}
                      checked={groupState === 'checked'}
                      ref={(el) => {
                        if (el) el.dataset.state = groupState;
                      }}
                      onCheckedChange={() => {
                        if (group.children.length > 0) {
                          toggleGroup(group);
                        } else {
                          toggleSection(group.id);
                        }
                      }}
                    />
                    <Label
                      htmlFor={`section-${group.id}`}
                      className="text-sm font-semibold text-gray-800 dark:text-gray-200 cursor-pointer"
                    >
                      {group.label}
                    </Label>
                  </div>

                  {/* Children */}
                  {group.children.length > 0 && (
                    <div className="ml-6 space-y-1.5">
                      {group.children.map((child) => (
                        <div key={child.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`section-${child.id}`}
                            checked={isChecked(child.id)}
                            onCheckedChange={() => toggleSection(child.id)}
                          />
                          <Label
                            htmlFor={`section-${child.id}`}
                            className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer"
                          >
                            {child.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalSectionCheckboxGrid;
export { SECTION_GROUPS, ALL_IDS };
